import type {
    DatePickerDayClickEventHandler,
    DatePickerDisabledDatesMatcher,
    DatePickerSelectRangeEventHandler,
} from '@/core/features/date-picker/date-picker-types';
import type { DatePickerDateRange } from '@/core/features/date-picker/date-picker-types';

import React from 'react';

import { DatePicker } from '@/core/features';
import { isSameDate } from '@/core/features/date/date';
import { datePickerLocale } from '@/core/features/date-picker/date-picker';
import DatePickerVerticalHeader from '@/core/features/date-picker/date-picker-vertical/date-picker-vertical-header';
import color from '@/core/features/styles/color';

export type DatePickerVerticalProps = {
    dateRangeArray: Date[];
    disabled: DatePickerDisabledDatesMatcher;
    hasNoHeader?: boolean;
    hasStickyHeader?: boolean;
    headerBackgroundColor?: string;
    headerSlot?: React.ReactElement;
    headerTextColor?: string;
    selectedDate?: DatePickerDateRange;
} & (
    | {
          onDateSelect: DatePickerSelectRangeEventHandler;
          onDayClick?: DatePickerDayClickEventHandler;
      }
    | {
          onDateSelect?: DatePickerSelectRangeEventHandler;
          onDayClick: DatePickerDayClickEventHandler;
      }
);

export default function DatePickerVertical({
    dateRangeArray,
    disabled,
    hasNoHeader = false,
    hasStickyHeader = false,
    headerBackgroundColor,
    headerSlot,
    headerTextColor,
    onDateSelect = () => {},
    onDayClick,
    selectedDate,
}: DatePickerVerticalProps) {
    const isSingle = !selectedDate?.to || (selectedDate?.from && isSameDate(selectedDate.from, selectedDate.to));

    return (
        <div className={'bg-white'}>
            {!hasNoHeader && (
                <DatePickerVerticalHeader
                    hasStickyHeader={hasStickyHeader}
                    headerBackgroundColor={headerBackgroundColor}
                    headerSlot={headerSlot}
                    headerTextColor={headerTextColor}
                />
            )}
            <div className={'datePickerVertical width-100 margin-top-15'}>
                <DatePicker
                    autoFocus
                    classNames={{
                        caption_label: 'caption_label',
                        day: 'day',
                        day_button: 'day_button',
                        hidden: 'hiddenDay',
                        months: 'months',
                        outside: 'outside',
                        range_end: 'range_end',
                        range_middle: 'range_middle',
                        range_start: 'range_start',
                        root: 'root',
                        selected: `selected ${isSingle ? 'isSingle' : ''}`,
                        today: 'today',
                    }}
                    disableNavigation={true}
                    disabled={disabled}
                    hideNavigation={true}
                    hideWeekdays={true}
                    locale={datePickerLocale}
                    min={1}
                    mode={'range'}
                    numberOfMonths={dateRangeArray.length}
                    onDayClick={onDayClick}
                    onSelect={onDateSelect}
                    required={true}
                    selected={selectedDate}
                />
                <style
                    global
                    jsx
                >{`
                    .datePickerVertical .root {
                        margin: 0;
                        padding: 0 10px;
                    }

                    .datePickerVertical .root table {
                        max-width: 100%;
                        width: 100%;
                        border-collapse: separate;
                        border-spacing: 0 4px;
                    }

                    .datePickerVertical .months {
                        display: block;
                    }

                    .datePickerVertical .tabs {
                        display: flex;
                    }

                    .datePickerVertical .caption_label {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        font-size: 16px;
                        font-weight: bold;
                        margin: 15px 0;
                    }

                    .datePickerVertical .day {
                        height: 46px;
                        padding: 0;
                        text-align: center;
                    }

                    .datePickerVertical .day.hiddenDay.outside {
                        background-color: ${color('white')};
                    }

                    .datePickerVertical .day_button {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        width: 100%;
                        font-size: 14px;
                    }

                    .datePickerVertical .day_button:disabled {
                        pointer-events: none;
                    }

                    .datePickerVertical .day_button:not([disabled]) {
                        cursor: pointer;
                    }

                    .datePickerVertical .day_button[disabled]:not(.selected) {
                        opacity: 0.25;
                    }

                    .datePickerVertical .day.range_middle button:disabled {
                        color: ${color('check24Blue')};
                        opacity: 1;
                    }

                    body.body--desktop .datePickerVertical .day.range_middle button:disabled {
                        color: ${color('white')};
                        opacity: 1;
                    }

                    body.body--desktop .datePickerVertical .day.range_middle:hover button:disabled {
                        color: ${color('check24Blue')};
                    }

                    .datePickerVertical .day.range_end button:disabled {
                        color: ${color('white')};
                        opacity: 1;
                    }

                    .datePickerVertical .selected {
                        color: ${color('white')};
                        background-color: ${color('endeavour')};
                    }

                    .datePickerVertical .selected.isSingle {
                        border-radius: 8px;
                    }

                    body.body--desktop .datePickerVertical .selected.isSingle {
                        border-radius: 3px;
                        background-color: ${color('check24Blue')};
                    }

                    .datePickerVertical .selected.outside {
                        background-color: ${color('white')};
                    }

                    body.body--desktop .datePickerVertical .selected.outside {
                        background-color: ${color('white')};
                    }

                    .datePickerVertical .today:not(.outside) {
                        font-weight: bold;
                    }

                    .datePickerVertical .range_start {
                        color: ${color('white')};
                        background-color: ${color('endeavour')};
                        border-radius: 8px 0 0 8px;
                    }

                    body.body--desktop .datePickerVertical .range_start {
                        background-color: ${color('check24Blue')};
                        border-radius: 3px 0 0 3px;
                    }

                    .datePickerVertical .range_middle {
                        color: ${color('mineShaft')};
                        background-color: ${color('hawkesBlue')};
                        border-radius: 0;
                    }

                    body.body--desktop .datePickerVertical .range_middle {
                        background-color: ${color('check24Blue-02')};
                    }

                    body.body--desktop .datePickerVertical .range_middle:hover {
                        color: ${color('check24Blue')};
                    }

                    .datePickerVertical .range_end {
                        color: ${color('white')};
                        background-color: ${color('endeavour')};
                        border-radius: 0 8px 8px 0;
                    }

                    body.body--desktop .datePickerVertical .range_end {
                        background-color: ${color('check24Blue')};
                        border-radius: 0 3px 3px 0;
                    }

                    .datePickerVertical .footer {
                        margin: 0.5em;
                    }
                `}</style>
            </div>
        </div>
    );
}
